import {yupResolver} from '@hookform/resolvers/yup';
import {showNotification} from 'platform/components';
import {HStack, Icon, Right, Space} from 'platform/foundation';

import {ChangeEvent, ReactElement, useEffect, useRef, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';

import {
  useGetCebiaAutotracerAndReportConfigQuery,
  usePatchCebiaAutotracerAndReportConfigMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {Button, CebiaAutotracerAndReportCredentials, TextField} from '@dms/teas';

import {useOnMount} from 'shared/web';

import {WrapperCard} from './styles';
import {cebiaAuthValidations} from './validations';

export const CebiaAuthentication = (): ReactElement => {
  const [showPassword, setViewPassword] = useState(false);
  const {data: cebiaAutotraceAndReport} = useGetCebiaAutotracerAndReportConfigQuery();
  const [patchCebiaAutotracerAndReportConfig] = usePatchCebiaAutotracerAndReportConfigMutation();
  const form = useForm<CebiaAutotracerAndReportCredentials>({
    resolver: yupResolver(cebiaAuthValidations()),
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    shouldFocusError: false,
    defaultValues: {
      username: '',
      password: '',
    },
  });
  const {
    formState,
    reset,
    control,
    formState: {errors},
  } = form;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useOnMount(() => {
    const {hash} = window.location;

    // eslint-disable-next-line security/detect-possible-timing-attacks -- Muted pre-existing violation
    if (hash === '#cebia-login') {
      wrapperRef?.current?.scrollIntoView();
      (
        wrapperRef?.current?.querySelector('input[name="username"]') as HTMLInputElement | null
      )?.focus();
    }
  });

  useEffect(
    function resetCredentialsForm() {
      reset({
        username: cebiaAutotraceAndReport?.credentials?.username ?? '',
        password: '',
      });
    },
    [cebiaAutotraceAndReport?.credentials?.username, reset]
  );

  const onValid: SubmitHandler<CebiaAutotracerAndReportCredentials> = (credentials) => {
    patchCebiaAutotracerAndReportConfig({
      cebiaAutotracerAndReportConfigRequestBody: {credentials},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  return (
    <WrapperCard
      ref={wrapperRef}
      id="cebia-authentication"
      title={i18n.t('general.labels.accessDataAuth')}
    >
      <HStack spacing={4}>
        <Controller
          name="username"
          control={control}
          render={({field: {onChange, value, ...rest}}) => (
            <TextField
              {...rest}
              type="text"
              value={value ?? ''}
              label={i18n.t('entity.user.labels.userName')}
              error={Boolean(errors.username?.message)}
              helperText={errors.username?.message}
              onChange={(e) => onChange(removeSpace(e))}
              data-testid="cebia-autotracer-auth-form-field-username"
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({field: {onChange, value, ...rest}}) => (
            <TextField
              {...rest}
              type={showPassword || !formState.isDirty ? 'text' : 'password'}
              label={i18n.t('general.labels.password')}
              placeholder="•••••"
              value={value ?? ''}
              error={Boolean(errors.password?.message)}
              helperText={errors.password?.message}
              endAdornment={{
                disableFocusToInput: true,
                Icon: showPassword ? (
                  <Icon value="action/visibility" />
                ) : (
                  <Icon value="action/visibility_off" />
                ),
                onClick: () => setViewPassword(!showPassword),
              }}
              onChange={(e) => onChange(removeSpace(e))}
              data-testid="cebia-autotracer-auth-form-field-password"
            />
          )}
        />
      </HStack>
      <Space vertical={4} />
      <Right>
        <Button
          primary
          disabled={!formState.isValid}
          onClick={onSubmit}
          data-testid={testIds.settings.vehicleServicesCebia('authSaveButton')}
        >
          {i18n.t('general.actions.save')}
        </Button>
      </Right>
    </WrapperCard>
  );
};

const removeSpace = (e: ChangeEvent<HTMLInputElement>) => e.target.value.replace(/\s+/g, '');
